import React, { createContext, useMemo } from "react";

/*
  WhatsApp delivery state of one message, for its ticks and its failed (i)
  indicator (#15964). Provided only for WhatsApp chats: every other channel
  gets no provider, so its bubbles render exactly as before.
*/
export type WhatsappTick = "sent" | "delivered" | "seen" | null;

export interface WhatsappMessageStatus {
  incoming: boolean;
  failed: boolean;
  error: string;
  tick: WhatsappTick;
  sent?: string;
  delivered?: string;
  seen?: string;
}

/* The row fields the status needs, copied onto a history message only when present,
   so rows without them (old rows, other channels) map exactly as before. */
export const whatsappStatusFields = (row: any) => ({
  ...(row?.send_status !== undefined && { send_status: row.send_status }),
  ...(row?.send_error && { send_error: row.send_error }),
  ...(row?.delivery_status && { delivery_status: row.delivery_status }),
});

export const WhatsappMessageStatusContext = createContext<WhatsappMessageStatus | null>(null);

export const getWhatsappStatus = (message: any, channel?: string): WhatsappMessageStatus | null => {
  if (channel !== "whatsapp" || !message) return null;
  const delivery = message.delivery_status || {};
  const incoming = !!message.isUser;
  const failed = !incoming && (message.send_status === false || !!delivery.failed);
  // No tick unless the data says so: a customer's message has reached us (delivered);
  // ours is sent / delivered / seen only once that is recorded.
  let tick: WhatsappTick = null;
  if (incoming) tick = "delivered";
  else if (!failed && delivery.seen) tick = "seen";
  else if (!failed && delivery.delivered) tick = "delivered";
  else if (!failed && message.send_status === true) tick = "sent";
  return {
    incoming,
    failed,
    error: message.send_error || delivery.error || "Message not delivered",
    tick,
    sent: message.time,
    delivered: incoming ? message.time : delivery.delivered,
    seen: delivery.seen,
  };
};

export const WhatsappMessageStatusProvider: React.FC<{
  message: any;
  channel?: string;
  children: React.ReactNode;
}> = ({ message, channel, children }) => {
  const value = useMemo(() => getWhatsappStatus(message, channel), [message, channel]);
  if (!value) return <>{children}</>;
  return (
    <WhatsappMessageStatusContext.Provider value={value}>
      {children}
    </WhatsappMessageStatusContext.Provider>
  );
};
